import { useMemo, useState } from 'react';
import BordUploaderButton from 'components/NudosComponents/BordUploaderButton/BordUploaderButton';
import {
  ACCEPTED_FILE_EXTENSION,
  BORD_UPLOADER_BUTTON_TYPE,
  ICON_VARIANT
} from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import { BILLING_PROVIDER_TYPE } from 'components/BillingDataTab/Components/BordInvoiceInfo/BordInvoiceInfo';
import { uploadProofOfPayment } from 'services/billing/storageBillingInvoices.service';
import { IStorageBillingInvoices, PAYMENT_METHODS } from 'types/billing.types';
import { formatCost } from 'utils/formatNumbers';
import { paymentMethodLabelsValue } from 'utils/variablesToTranslate';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import './PaymentInfo.scss';

interface IPaymentInfoProps {
  billingInvoice?: IStorageBillingInvoices;
  isLoading: boolean;
  handleUpdateProof: () => void;
}
const PaymentInfo = ({ billingInvoice, isLoading, handleUpdateProof }: IPaymentInfoProps) => {
  const { paymentType, amount, currency, currencyName, selectedPaymentMethod, proofOfPaymentUrl, billingMethod } =
    billingInvoice || {};
  const currencyNameLower = currencyName?.toLowerCase();
  const isFilled = !!proofOfPaymentUrl;
  const [isUploading, setIsUploading] = useState(false);

  const paymentAutomationText = useMemo(() => {
    switch (billingMethod?.billingProvider) {
      case BILLING_PROVIDER_TYPE.MANUAL:
      case BILLING_PROVIDER_TYPE.BORD:
        return '(Manual)';
      default:
        return '(Automatizada)';
    }
  }, [billingMethod?.billingProvider]);

  const getPaymentMethodName = (paymentType: string) => {
    return paymentMethodLabelsValue?.find(item => item?.value === paymentType)?.name || '';
  };

  const paymentMethodName = useMemo(() => {
    if (selectedPaymentMethod?.toLowerCase().includes(PAYMENT_METHODS.CARD)) {
      return PAYMENT_METHODS.CREDIT_CARD;
    }

    return getPaymentMethodName(paymentType || '');
  }, [paymentType, selectedPaymentMethod]);

  const handleDownloadFile = () => {
    if (!proofOfPaymentUrl) return;
    window.open(proofOfPaymentUrl, '_blank');
  };

  const handleChangeFile = async (file: File) => {
    if (!file) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadProofOfPayment(billingInvoice?.storageInvoiceId || 0, formData);
      if (response) {
        handleUpdateProof();
        displaySuccessNotification({
          customJSXMessage: <>Comprobante de pago actualizado correctamente</>
        });
      }
    } catch {
      displayErrorNotification({ customJSXMessage: <>Ha ocurrido un error</> });
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) return <div className="paymentInfoSkeleton animationLoader" />;
  return (
    <div className="paymentInfo">
      <div className="paymentInfoHeader">
        <span className="paymentInfoHeaderTitle">Información de pago</span>
      </div>
      <div className="internalDivider" />
      <div className="paymentInfoBody">
        <div className="paymentInfoItem">
          <span className="paymentInfoItemTitle">Método de pago</span>
          <span className="paymentInfoItemContent">
            {' '}
            {paymentMethodName || ''} <span className="paymentAutomationText">{paymentAutomationText}</span>
          </span>
        </div>

        <div className="paymentInfoItem">
          <span className="paymentInfoItemTitle">Monto en {currencyNameLower} </span>
          <span className="paymentInfoItemContent">
            {formatCost(amount, 2)} {currency}
          </span>
        </div>
      </div>
      <BordUploaderButton
        isFilled={isFilled}
        disabledBordUploaderButton={false}
        bordUploaderButtonType={BORD_UPLOADER_BUTTON_TYPE.DEFAULT}
        title={
          <span>
            <u>Busca</u> o arrastra el comprobante
          </span>
        }
        emptyBordUploaderButtonSubtitle="Formatos aceptados: JPG, PDF Peso máximo: 10mb"
        defaultFile={{
          documentName: 'Comprobante.pdf',
          url: proofOfPaymentUrl || ''
        }}
        modeReplaceFile
        handleDownloadFile={handleDownloadFile}
        customIconChangeFile={ICON_VARIANT.REFRESH}
        handleChangeFile={handleChangeFile}
        customClassName="paymentInfoDocContainer"
        customContainerClassName="paymentInfoDoc"
        customFileNameClass="paymentInfoDocFileName"
        iconsCustomWidth="14px"
        loading={isUploading}
        acceptFileExtension={[ACCEPTED_FILE_EXTENSION.PDF, ACCEPTED_FILE_EXTENSION.JPG]}
      />
    </div>
  );
};

export default PaymentInfo;
